'use client';

import type { ResultOf } from '@graphql-typed-document-node/core';
import { useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Button } from '@savvy-kit/components/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@savvy-kit/components/input-group';
import { Spinner } from '@savvy-kit/components/spinner';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@savvy-kit/components/table';
import { isNil } from 'es-toolkit';
import { SearchIcon } from 'lucide-react';
import { graphql } from 'src/gql';
import { useDebounce } from 'src/hooks/use-debounce';
import { formatDateToAmericanStyle } from 'src/utils/formatting';
import { handleMutationError } from 'src/utils/handle-mutation-error';

import { useQueryWithErrorLogging } from '@savvy/graphql-hooks/useQueryWithErrorLogging';

import { useHouseholdArchive } from '../hooks/use-household-archive';

const ArchivedClientsQuery = graphql(/* GraphQL */ `
  query ArchivedClients($query: String, $inactiveOnly: Boolean) {
    advisorDashboardClients(query: $query, inactiveOnly: $inactiveOnly) {
      edges {
        node {
          id
          name
          lastContactAt
          salesforceAccountId
        }
      }
    }
  }
`);

export function ArchivedClientsTable() {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 350);

  const { handleUnarchive } = useHouseholdArchive();

  const { data, loading } = useQueryWithErrorLogging(ArchivedClientsQuery, {
    variables: {
      query: debouncedSearch === '' ? undefined : debouncedSearch,
      inactiveOnly: true,
    },
  });

  const households = useMemo(() => {
    const edges = data?.advisorDashboardClients.edges;
    if (isNil(edges)) return [];
    return edges.flatMap((edge) => (!isNil(edge?.node) ? [edge.node] : []));
  }, [data]);

  return (
    <div className="flex h-full min-h-0 flex-col border border-light rounded-md">
      <div className="shrink-0 p-4 border-b border-light">
        <InputGroup className="w-2/3">
          <InputGroupAddon>
            <SearchIcon />
          </InputGroupAddon>
          <InputGroupInput
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search for a household"
            value={search}
          />
        </InputGroup>
      </div>
      <Table className="[&_th]:px-4 [&_td]:px-4" containerClassName="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
        <TableHeader>
          <TableRow>
            <TableHead>Household</TableHead>
            <TableHead>Last contact</TableHead>
            <TableHead>&nbsp;</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <ArchivedClientsRows households={households} loading={loading} onRestore={handleUnarchive} />
        </TableBody>
      </Table>
    </div>
  );
}

type ArchivedHousehold = NonNullable<
  NonNullable<ResultOf<typeof ArchivedClientsQuery>['advisorDashboardClients']['edges']>[number]
>['node'];

type ArchivedClientsRowsProps = {
  readonly households: readonly NonNullable<ArchivedHousehold>[];
  readonly loading: boolean;
  readonly onRestore: (id: string, name: string) => Promise<void>;
};

function ArchivedClientsRows({ households, loading, onRestore }: ArchivedClientsRowsProps) {
  const router = useRouter();

  const [restoringIds, setRestoringIds] = useState<string[]>([]);

  const handleRestore = (id: string, name: string) => async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    try {
      setRestoringIds((prev) => [...prev, id]);
      await onRestore(id, name);
    } catch (error) {
      console.error('Failed to unarchive client', error);
      handleMutationError(error, 'Failed to restore client');
    } finally {
      setRestoringIds((prev) => prev.filter((restoringId) => restoringId !== id));
    }
  };

  if (loading && households.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={3} className="text-center text-secondary">
          Loading...
        </TableCell>
      </TableRow>
    );
  }

  if (households.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={3} className="text-center text-secondary">
          No archived clients found
        </TableCell>
      </TableRow>
    );
  }

  return households.map((household) => (
    <TableRow
      key={household.id}
      className="cursor-pointer"
      onClick={() => {
        router.push(`/advisor/dashboard/client/${household.salesforceAccountId}`);
      }}
    >
      <TableCell>{household.name}</TableCell>
      <TableCell>
        {!isNil(household.lastContactAt) ? formatDateToAmericanStyle(household.lastContactAt) : '—'}
      </TableCell>
      <TableCell>
        <Button
          className="w-17.5"
          disabled={restoringIds.includes(household.id)}
          onClick={handleRestore(household.id, household.name)}
          size="sm"
          variant="secondary"
        >
          {restoringIds.includes(household.id) ? <Spinner /> : 'Restore'}
        </Button>
      </TableCell>
    </TableRow>
  ));
}
